// src/pages/ResourcesPage.jsx - Profound Philosophy
// Curated books, free online resources, and videos/podcasts for going deeper
// into each module's ideas. Mirrors the resources-page pattern shared across
// the DDD educational apps (category tabs, search, per-category card layouts).
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen,
  Youtube,
  Globe,
  ExternalLink,
  Star,
  Search,
  Sparkles,
  Award,
  Eye,
  Brain,
  GraduationCap,
  Landmark
} from 'lucide-react';

const ResourcesPage = () => {
  const [activeCategory, setActiveCategory] = useState('books');
  const [searchTerm, setSearchTerm] = useState('');

  // Books - one anchor text per module, plus a few that span the whole journey.
  // Claims re-checked in the October 2026 authenticity audit; see
  // docs/audits/authenticity-audit-profound-philosophy-2026-10.md. The old
  // `rating` and `pageCount` numbers were never sourced and were never rendered,
  // so they are gone.
  const books = [
    {
      title: "Sophie's World",
      author: "Jostein Gaarder",
      credentials: "Norwegian author and former secondary-school teacher",
      year: "1991",
      description: "A Norwegian teenager receives mysterious letters that walk her through the history of Western philosophy, inside a novel that becomes its own philosophical puzzle.",
      whyRelevant: "The friendliest possible companion to Module 1 (What is Philosophy). It walks the Western lineage the modules draw on, in order, wrapped in an actual story you'll want to finish.",
      keyTakeaway: "Philosophy is a living conversation stretching from Thales to now, and the strangest thing in the universe is that anything exists to wonder about it at all.",
      modules: [1],
      category: "Start Here",
      purchaseLink: "https://www.amazon.com.au/s?k=sophie%27s+world+jostein+gaarder"
    },
    {
      title: "The Problems of Philosophy",
      author: "Bertrand Russell",
      credentials: "Philosopher and logician; Nobel Prize in Literature, 1950",
      year: "1912",
      description: "Russell's short, sharp introduction to the central puzzles of knowledge and reality: appearance versus reality, induction, truth, and the limits of what can be known.",
      whyRelevant: "The classic companion to Module 3 (Epistemology). More than a century old and still the cleanest hundred-odd pages on what you can actually know.",
      keyTakeaway: "Russell's closing chapter: philosophy is studied for the questions themselves, because they enlarge our sense of what is possible and free us from what he calls \"the tyranny of custom\".",
      modules: [1, 3],
      category: "Epistemology",
      purchaseLink: "https://www.amazon.com.au/s?k=the+problems+of+philosophy+bertrand+russell"
    },
    {
      title: "A Rulebook for Arguments",
      author: "Anthony Weston",
      credentials: "Philosopher who taught at Elon University",
      year: "1986 (fifth edition 2018)",
      description: "A pocket-sized manual of the rules of good reasoning: short arguments, generalisations, analogies, sources, deduction and the classic fallacies, each with crisp examples.",
      whyRelevant: "The perfect desk companion to Module 2 (Logic & Reasoning) and the Argument Builder tool. Everything the module teaches, condensed into rules you can check your own writing against.",
      keyTakeaway: "Good arguments are built, not found: distinguish premises from conclusion, argue from reliable sources, consider objections, and never mistake loudness for logic.",
      modules: [2],
      category: "Logic",
      purchaseLink: "https://www.amazon.com.au/s?k=a+rulebook+for+arguments+anthony+weston"
    },
    {
      title: "Meditations on First Philosophy",
      author: "René Descartes",
      credentials: "Philosopher and mathematician, often called the father of modern philosophy",
      year: "1641",
      description: "Descartes demolishes everything he believes and rebuilds from the one thing he cannot doubt: that he is doubting. Six short meditations that reset Western philosophy.",
      whyRelevant: "Primary source for Module 3 (Epistemology) and Module 5 (Metaphysics). The dream argument and the cogito are best met in Descartes' own voice, and the voice is surprisingly readable.",
      keyTakeaway: "\"I am, I exist\" (Second Meditation): even a deceiving demon cannot make you wrong about that while you are thinking it. The better-known \"I think, therefore I am\" is from his earlier Discourse on the Method (1637). Everything else needs rebuilding from there.",
      modules: [3, 5],
      category: "Primary Text",
      purchaseLink: "https://www.amazon.com.au/s?k=meditations+on+first+philosophy+descartes"
    },
    {
      title: "Justice: What's the Right Thing to Do?",
      author: "Michael Sandel",
      credentials: "Bass Professor of Government, Harvard University",
      year: "2009",
      description: "Built from Sandel's long-running Harvard course, the first the university put online for free. He runs utilitarianism, libertarianism, Kant, Rawls and Aristotle head-to-head on real dilemmas, from price gouging to conscription.",
      whyRelevant: "The bridge between Module 4 (Ethics) and Module 7 (Social & Political Philosophy). Reads like the Political Philosophy Mapper tool in book form: frameworks tested on live cases.",
      keyTakeaway: "Justice is unavoidably judgemental: deciding what's fair means deciding what virtues deserve honour and what a good life is, questions markets alone can't answer.",
      modules: [4, 7],
      category: "Ethics & Politics",
      purchaseLink: "https://www.amazon.com.au/s?k=justice+what%27s+the+right+thing+to+do+sandel"
    },
    {
      title: "Nicomachean Ethics",
      author: "Aristotle",
      credentials: "Ancient Greek philosopher, student of Plato",
      year: "4th century BCE",
      description: "Aristotle's field guide to the good life: happiness as flourishing, virtue as a habit lying between extremes, and friendship as the crown of a life well lived.",
      whyRelevant: "The foundation of the virtue ethics lens in Module 4 and the Ethical Framework Analyser. Twenty-three centuries later, it's still the best account of character as practice.",
      keyTakeaway: "We become just by doing just acts and brave by doing brave acts (Book II). The line often pinned to Aristotle, that excellence is not an act but a habit, is Will Durant's summary of this passage in The Story of Philosophy (1926). Happiness, for Aristotle, is activity in accordance with virtue (Book I).",
      modules: [4],
      category: "Primary Text",
      purchaseLink: "https://www.amazon.com.au/s?k=nicomachean+ethics+aristotle"
    },
    {
      title: "The Mind's I",
      author: "Douglas Hofstadter & Daniel Dennett",
      credentials: "Cognitive scientist and philosopher of mind, editing with commentary",
      year: "1981",
      description: "A curated anthology of stories and essays on self and soul, including Searle's Chinese Room paper with the editors' replies, each piece followed by playful, probing reflections.",
      whyRelevant: "The companion volume to Module 6 (Philosophy of Mind) and the Chinese Room tool. You get the famous thought experiments in their original words, plus the arguments they started.",
      keyTakeaway: "The self may be a story the brain tells itself, and the question 'could a machine think?' turns uncomfortably quickly into 'how do you know that you do?'",
      modules: [5, 6],
      category: "Philosophy of Mind",
      purchaseLink: "https://www.amazon.com.au/s?k=the+mind%27s+i+hofstadter+dennett"
    },
    {
      title: "The Myth of Sisyphus",
      author: "Albert Camus",
      credentials: "Philosopher and novelist; Nobel Prize in Literature, 1957. Justin O'Brien's English translation (1955) is the one quoted in this app",
      year: "1942",
      description: "Camus confronts the absurd, the clash between our demand for meaning and the universe's silence, and argues for revolt, freedom and passion rather than despair.",
      whyRelevant: "The heart of Module 8 (Existentialism & Meaning) and the Meaning Creation Workshop. Short enough for a weekend, heavy enough to change how Monday feels.",
      keyTakeaway: "In O'Brien's translation: \"There is no fate that can not be surmounted by scorn.\" And the book's last line: \"One must imagine Sisyphus happy.\"",
      modules: [8],
      category: "Existentialism",
      purchaseLink: "https://www.amazon.com.au/s?k=the+myth+of+sisyphus+camus"
    },
    {
      title: "Meditations",
      author: "Marcus Aurelius",
      credentials: "Roman emperor and Stoic; Gregory Hays's translation (2002) is the one linked here",
      year: "c. 170–180 CE",
      description: "An emperor's private notebook, never meant for publication, reminding himself to be patient, honest, unafraid of death, and focussed only on what he can control.",
      whyRelevant: "Philosophy as daily practice, the spirit behind the whole toolkit. Pairs beautifully with Module 8 and with any tool you use more than once.",
      keyTakeaway: "In Hays's rendering of Meditations 5.20: \"The impediment to action advances action. What stands in the way becomes the way.\" The popular line about having power over your mind but not over outside events is not in any known translation; the nearest the book comes is 4.3, on disturbance coming only from within.",
      modules: [1, 8],
      category: "Practice",
      purchaseLink: "https://www.amazon.com.au/s?k=meditations+marcus+aurelius+gregory+hays"
    },
    {
      title: "The Masnavi, Book One",
      author: "Rumi (translated by Jawid Mojaddedi)",
      credentials: "13th-century Persian Sufi poet; verse translation by Jawid Mojaddedi of Rutgers University, Oxford World's Classics, 2004",
      year: "13th century (translation 2004)",
      description: "The opening book of Rumi's vast spiritual poem, which begins with the reed flute cut from the reed bed, its song the cry of separation and the longing to return.",
      whyRelevant: "Module 8 gives Rumi the counter-voice to Camus; this is the real thing. A faithful scholarly translation matters here: much of what circulates online under Rumi's name is not Rumi.",
      keyTakeaway: "Longing itself can be read as evidence of belonging: for Rumi, you do not ache for a home you never had.",
      modules: [8],
      category: "Global Voices",
      purchaseLink: "https://www.amazon.com.au/s?k=masnavi+book+one+mojaddedi+oxford"
    },
    {
      title: "The Analects",
      author: "Confucius (translated by D. C. Lau)",
      credentials: "Sayings of the Chinese teacher, traditionally compiled by his followers after his death in 479 BCE; D. C. Lau's Penguin Classics translation (1979)",
      year: "compiled in the centuries after 479 BCE",
      description: "Short exchanges and observations on cultivating character, performing your relationships well, and becoming the kind of person others can trust: the junzi.",
      whyRelevant: "Module 4 sets Confucius beside Aristotle as the same wager made half a world away: you become good by practising good. This is the source.",
      keyTakeaway: "There is no self to perfect apart from your relationships; virtue is excellence in the roles you already occupy.",
      modules: [4],
      category: "Global Voices",
      purchaseLink: "https://www.amazon.com.au/s?k=analects+confucius+d+c+lau+penguin"
    },
    {
      title: "Zhuangzi: Basic Writings",
      author: "Zhuangzi (translated by Burton Watson)",
      credentials: "Daoist text of the Warring States period; Burton Watson's translation, Columbia University Press (1964, revised 2003)",
      year: "4th century BCE",
      description: "Playful, vertiginous parables, including the butterfly dream, that dissolve the certainties most philosophy tries to defend.",
      whyRelevant: "Module 3's Great Doubt meets Zhuangzi dreaming he was a butterfly, some two thousand years before Descartes' demon. He is funnier than Descartes, and just as deep.",
      keyTakeaway: "Some questions are not to be answered but inhabited: Zhuangzi declines to say which one is the dream, and means it.",
      modules: [3],
      category: "Global Voices",
      purchaseLink: "https://www.amazon.com.au/s?k=zhuangzi+basic+writings+burton+watson"
    },
    {
      title: "What the Buddha Taught",
      author: "Walpola Rahula",
      credentials: "Sri Lankan Buddhist monk and scholar; a widely used introduction for readers new to the tradition",
      year: "1959",
      description: "A clear, rigorous account of the Buddha's core teaching from the earliest texts, including the not-self doctrine and the five aggregates.",
      whyRelevant: "Module 5's identity puzzles arrive where the Buddha and Nagasena's chariot parked some twenty centuries earlier. This is the cleanest way into that tradition.",
      keyTakeaway: "The unchanging self the puzzles keep failing to find was never there, and for this tradition that is not a loss but a release.",
      modules: [5, 6],
      category: "Global Voices",
      purchaseLink: "https://www.amazon.com.au/s?k=what+the+buddha+taught+walpola+rahula"
    },
    {
      title: "Deliverance from Error",
      author: "Al-Ghazali (translated by R. J. McCarthy)",
      credentials: "Islamic theologian and philosopher (d. 1111); his intellectual autobiography, in Richard Joseph McCarthy's translation",
      year: "early 12th century",
      description: "Al-Ghazali's own account of doubting his senses, then reason itself: a sceptical crisis five centuries before Descartes, with a different exit.",
      whyRelevant: "Module 3 tells his story beside Descartes and Zhuangzi; here is the remarkable original, in his own voice.",
      keyTakeaway: "The deepest doubt may not be answered by argument. Al-Ghazali's crisis lifted, he says, by a light God cast into his breast rather than by a proof.",
      modules: [3],
      category: "Global Voices",
      purchaseLink: "https://www.amazon.com.au/s?k=al-ghazali+deliverance+from+error+mccarthy"
    },
    {
      title: "The Ethics of Ambiguity",
      author: "Simone de Beauvoir",
      credentials: "Existentialist philosopher; her ethics in full",
      year: "1947",
      description: "Beauvoir's answer to the charge that existentialism is a philosophy of heroic loners: to will your own freedom honestly, you must will everyone else's.",
      whyRelevant: "Module 8 gives Beauvoir her overdue place; this short book is where her situated-freedom ethics lives in full.",
      keyTakeaway: "Freedom is always exercised from somewhere, and it binds: my freedom and yours are not separate projects.",
      modules: [8],
      category: "Existentialism",
      purchaseLink: "https://www.amazon.com.au/s?k=ethics+of+ambiguity+beauvoir"
    },
    {
      title: "No Future Without Forgiveness",
      author: "Desmond Tutu",
      credentials: "Archbishop of Cape Town (1986 to 1996) and chair of South Africa's Truth and Reconciliation Commission",
      year: "1999",
      description: "Tutu's account of the Commission's work: Ubuntu's repair-leaning justice carried into one of history's hardest tests.",
      whyRelevant: "Module 7 introduces Ubuntu as the communal answer in the justice debate; this is what it looked like in practice, told by the man who chaired it.",
      keyTakeaway: "A person is a person through other persons, and a wounded society heals through truth-telling and repair, not only punishment.",
      modules: [7],
      category: "Global Voices",
      purchaseLink: "https://www.amazon.com.au/s?k=no+future+without+forgiveness+tutu"
    }
  ];

  // Free online resources - the open front doors of academic philosophy
  const online = [
    {
      title: "Stanford Encyclopedia of Philosophy",
      authors: "Each entry written and maintained by a specialist, refereed by an editorial board",
      institution: "Stanford University",
      year: "1995-present",
      description: "The gold standard of philosophy reference: refereed, continually updated encyclopedia entries on most topics and thinkers the modules touch, free.",
      keyFindings: "Nearly 1,800 in-depth entries (the SEP's own count, 2023), each written and maintained by a specialist. When you want the serious version of any module topic, it's here.",
      whyRelevant: "Every module has a shelf of SEP entries behind it: 'Epistemology', 'Consequentialism', 'Free Will', 'Personal Identity', 'The Meaning of Life'. Bookmark it; you'll be back.",
      modules: [1, 2, 3, 4, 5, 6, 7, 8],
      category: "Encyclopedia",
      link: "https://plato.stanford.edu"
    },
    {
      title: "Internet Encyclopedia of Philosophy",
      authors: "Written and edited by academic philosophers; founded by James Fieser",
      institution: "iep.utm.edu",
      year: "1995-present",
      description: "SEP's friendlier sibling: peer-reviewed articles pitched a notch more accessibly, often the better first stop when a topic is brand new to you.",
      keyFindings: "About 900 entries with a gentler on-ramp than SEP, including overviews of fallacies, ethics and the classic thought experiments.",
      whyRelevant: "When an SEP entry feels like a cold plunge, the IEP entry on the same topic is the heated pool. Ideal alongside Modules 2-4.",
      modules: [1, 2, 3, 4],
      category: "Encyclopedia",
      link: "https://iep.utm.edu"
    },
    {
      title: "1000-Word Philosophy",
      authors: "Various academic philosophers",
      institution: "Open-access essay anthology",
      year: "2014-present",
      description: "Original introductions to philosophical topics, each kept under 1,000 words: the perfect between-modules snack.",
      keyFindings: "More than 200 tight essays (the site's own count): the trolley problem, scepticism, free will, personal identity, the ethics of eating, each readable over a cuppa.",
      whyRelevant: "The essays map closely onto module topics and make excellent warm-ups before a toolkit session.",
      modules: [1, 2, 3, 4, 5, 6, 7, 8],
      category: "Essays",
      link: "https://1000wordphilosophy.com"
    },
    {
      title: "Project Gutenberg: Philosophy Bookshelf",
      authors: "Plato, Aristotle, Hume, Kant, Mill, Nietzsche and more",
      institution: "Project Gutenberg",
      year: "Free e-texts",
      description: "The out-of-copyright canon, free in every format: Plato's dialogues, Descartes, Mill, Russell's Problems of Philosophy and hundreds more.",
      keyFindings: "Most of the pre-twentieth-century texts the modules draw on are here at no cost: searchable, downloadable, yours.",
      whyRelevant: "Reading two pages of actual Plato beats reading twenty pages about him. Start with the Apology: Socrates on trial for doing philosophy.",
      modules: [1, 2, 3, 4, 5, 7],
      category: "Primary Texts",
      link: "https://www.gutenberg.org/ebooks/bookshelf/57"
    },
    {
      title: "The Ethics Centre (Australia)",
      authors: "Independent not-for-profit, founded in Sydney in 1989",
      institution: "Sydney, Australia",
      year: "Articles, events and Ethi-call",
      description: "Australia's home of applied ethics: plain-language articles on live moral questions, public events, and Ethi-call, a free counselling line for people facing an ethical dilemma. The other countries this app serves have their own applied-ethics bodies and university centres running similar public programmes.",
      keyFindings: "A large, searchable library explaining ethical concepts through current Australian debates, plus the Festival of Dangerous Ideas, which the Centre co-founded in 2009.",
      whyRelevant: "Module 4 made practical and local. When an ethics question stops being theoretical, this is where Australian thinking on it lives; readers elsewhere can treat it as a model of what to look for at home.",
      modules: [4, 7],
      category: "Applied Ethics",
      link: "https://ethics.org.au"
    }
  ];

  // Videos & podcasts - philosophy for ears and eyes
  const videos = [
    {
      title: "Crash Course Philosophy",
      presenter: "Hank Green",
      platform: "YouTube",
      duration: "46 episodes, ~10 mins each",
      year: "2016-17",
      description: "The complete undergraduate survey in animated ten-minute bites: logic, knowledge, mind, God, ethics, political philosophy and meaning, fast and funny.",
      whyWatch: "It's the video twin of this app's eight modules. Watch the matching episode after finishing a module and the ideas set like concrete.",
      keyLessons: "Every major argument gets a fair hearing before it gets critiqued: a model of the intellectual honesty Module 2 teaches.",
      modules: [1, 2, 3, 4, 5, 6, 7, 8],
      link: "https://www.youtube.com/playlist?list=PL8dPuuaLjXtNgK6MZucdYldNkMybYIHKR"
    },
    {
      title: "Philosophize This!",
      presenter: "Stephen West",
      platform: "Podcast (all platforms)",
      duration: "250+ episodes, ~30 mins each",
      year: "2013-present",
      description: "A self-taught podcaster walks through the history of philosophy in plain language with zero gatekeeping.",
      whyWatch: "The best long-form companion for commutes: warm and genuinely rigorous. Episodes on the Stoics, Kant and Camus pair directly with the modules.",
      keyLessons: "Philosophy is a 2,500-year conversation, and you understand each thinker better when you know who they were arguing with.",
      modules: [1, 3, 4, 5, 8],
      link: "https://www.philosophizethis.org"
    },
    {
      title: "Wireless Philosophy (Wi-Phi)",
      presenter: "Academic philosophers; the project began at Yale in 2013",
      platform: "YouTube / wi-phi.com",
      duration: "Short videos, 5-10 mins each",
      year: "2013-present",
      description: "Academic philosophers explain one concept per short video, from the structure of arguments to the trolley problem.",
      whyWatch: "The closest free thing to sitting in a tutorial. The critical thinking series is effectively Module 2 with a whiteboard.",
      keyLessons: "Big philosophical positions are built from small, checkable steps, and five minutes of careful argument beats an hour of vibes.",
      modules: [2, 3, 4, 6, 7],
      link: "https://www.wi-phi.com"
    },
    {
      title: "In Our Time",
      presenter: "Melvyn Bragg (1998 to 2025), now Misha Glenny, with three academic guests",
      platform: "BBC Radio 4 / Podcast",
      duration: "45-min episodes; more than 1,000 in the archive across all subjects",
      year: "1998-present",
      description: "Three scholars, one host, no script: forty-five minutes on one philosopher or one problem at a time, with a deep philosophy strand in the archive.",
      whyWatch: "When a module topic hooks you, there's a fair chance an In Our Time episode goes two levels deeper with actual specialists.",
      keyLessons: "Listening to experts disagree carefully teaches more about how philosophy works than any summary can.",
      modules: [1, 3, 4, 5, 8],
      link: "https://www.bbc.co.uk/programmes/b006qykl"
    },
    {
      title: "The Good Place",
      presenter: "Created by Michael Schur",
      platform: "NBC (2016-20); Netflix in many countries",
      duration: "4 seasons, 22-min episodes",
      year: "2016-2020",
      description: "A sitcom that smuggles a genuine moral philosophy course into the afterlife adventures of four flawed humans: Kant, Aristotle, the trolley problem, and T. M. Scanlon's What We Owe to Each Other, which Schur has called the spine of the show.",
      whyWatch: "Module 4's ethical frameworks, dramatised and hilarious. The trolley problem episode alone earns its place on this list.",
      keyLessons: "What we owe to each other can't be gamed by point-scoring; becoming good is a practice, done with and for other people.",
      modules: [4, 8],
      link: "https://en.wikipedia.org/wiki/The_Good_Place"
    }
  ];

  const categories = [
    { id: 'books', label: 'Books', icon: BookOpen, count: books.length },
    { id: 'online', label: 'Free Online', icon: Globe, count: online.length },
    { id: 'videos', label: 'Videos & Podcasts', icon: Youtube, count: videos.length }
  ];

  const getActiveResources = () => {
    let resources = [];
    switch (activeCategory) {
      case 'books': resources = books; break;
      case 'online': resources = online; break;
      case 'videos': resources = videos; break;
      default: resources = books;
    }

    if (!searchTerm) return resources;

    return resources.filter(resource =>
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (resource.author && resource.author.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (resource.authors && resource.authors.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (resource.presenter && resource.presenter.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (resource.description && resource.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const resources = getActiveResources();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gunmetal via-charcoal to-gunmetal pb-8 pt-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero: high cirrus backdrop: references, far up and reachable */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative text-center mb-12 rounded-3xl overflow-hidden py-10 px-4"
        >
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
              src="/images/cloud-resources.webp"
              alt=""
              className="w-full h-full object-cover object-[50%_45%] opacity-60"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gunmetal/55 via-gunmetal/30 to-gunmetal" />
          </div>
          <div className="relative">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 inline-flex items-center justify-center w-28 h-28 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 mx-auto"
            >
              <Landmark className="w-14 h-14 text-white" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-conversation-teal drop-shadow-[0_0_15px_rgba(20,184,166,0.5)]">
              Philosophy Resources
            </h1>
            <p className="text-xl text-pearl/80 max-w-2xl mx-auto">
              Curated books, free online libraries, and videos and podcasts for going deeper into every module
            </p>
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-pearl/60 w-5 h-5" />
            <input
              type="text"
              placeholder="Search resources..."
              aria-label="Search resources"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-charcoal border border-gunmetal/50 rounded-xl text-white placeholder-pearl/60 focus:outline-none focus:border-conversation-teal"
            />
          </div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  aria-pressed={isActive}
                  className={`flex items-center gap-3 px-6 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-conversation-teal text-charcoal shadow-lg'
                      : 'bg-charcoal text-pearl/70 hover:bg-gunmetal'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-semibold">{category.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isActive ? 'bg-white/20' : 'bg-gunmetal/50'
                  }`}>
                    {category.count}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Resource Cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <AnimatePresence>
            {resources.map((resource, index) => (
              <motion.div
                key={resource.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl p-6 hover:shadow-2xl transition-all border-2 ${
                  activeCategory === 'books'
                    ? 'bg-gradient-to-br from-conversation-teal/40 via-gunmetal to-conversation-blue/40 border-conversation-teal/60 hover:border-conversation-teal/80 hover:from-conversation-teal/50 hover:to-conversation-blue/50'
                    : activeCategory === 'online'
                    ? 'bg-gradient-to-br from-blue-500/40 via-gunmetal to-purple-500/40 border-blue-500/60 hover:border-blue-500/80 hover:from-blue-500/50 hover:to-purple-500/50'
                    : 'bg-gradient-to-br from-red-500/40 via-gunmetal to-pink-500/40 border-red-500/60 hover:border-red-500/80 hover:from-red-500/50 hover:to-pink-500/50'
                }`}
              >
                {/* Book Resources */}
                {activeCategory === 'books' && (
                  <>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-conversation-teal to-conversation-blue rounded-lg flex items-center justify-center shadow-lg">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-bright-gold mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-pearl/80 text-sm">{resource.author}</p>
                        {resource.credentials && (
                          <p className="text-pearl/75 text-xs italic">{resource.credentials}</p>
                        )}
                      </div>
                    </div>

                    <p className="text-pearl/80 leading-relaxed mb-4">
                      {resource.description}
                    </p>

                    <div className="bg-gradient-to-br from-conversation-blue/20 to-conversation-blue/5 border-l-4 border-conversation-blue rounded-lg p-4 mb-4 shadow-md">
                      <h4 className="text-sky-200 font-bold text-sm mb-2 flex items-center gap-2">
                        <Brain className="w-4 h-4" />
                        Why This Matters:
                      </h4>
                      <p className="text-pearl/80 text-sm leading-relaxed">{resource.whyRelevant}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-l-4 border-green-400 rounded-lg p-4 mb-4 shadow-md">
                      <h4 className="text-green-400 font-bold text-sm mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        Key Takeaway:
                      </h4>
                      <p className="text-pearl/80 text-sm leading-relaxed">{resource.keyTakeaway}</p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gunmetal/30">
                      <div className="flex gap-2 flex-wrap">
                        {resource.modules && resource.modules.map(moduleNum => (
                          <span key={moduleNum} className="px-3 py-1 bg-gradient-to-r from-purple-700 to-pink-700 text-white text-xs rounded-full font-semibold shadow-md">
                            Module {moduleNum}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-pearl/80">
                        <span>{resource.year}</span>
                      </div>
                    </div>

                    {resource.purchaseLink && (
                      <a
                        href={resource.purchaseLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-conversation-teal to-conversation-blue hover:from-conversation-teal/80 hover:to-conversation-blue/80 text-charcoal rounded-lg transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                      >
                        <span>Find on Amazon</span>
                        <span className="sr-only"> (opens in new tab)</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </>
                )}

                {/* Free Online Resources */}
                {activeCategory === 'online' && (
                  <>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                        <Globe className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-bright-gold mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-pearl/80 text-sm">{resource.authors}</p>
                        <p className="text-pearl/75 text-xs">{resource.institution} • {resource.year}</p>
                      </div>
                    </div>

                    <p className="text-pearl/80 leading-relaxed mb-4">
                      {resource.description}
                    </p>

                    <div className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border-l-4 border-blue-400 rounded-lg p-4 mb-4 shadow-md">
                      <h4 className="text-blue-400 font-bold text-sm mb-2 flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        What You'll Find:
                      </h4>
                      <p className="text-pearl/80 text-sm leading-relaxed">{resource.keyFindings}</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border-l-4 border-purple-400 rounded-lg p-4 mb-4 shadow-md">
                      <h4 className="text-purple-400 font-bold text-sm mb-2 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Why It's Worth Your Time:
                      </h4>
                      <p className="text-pearl/80 text-sm leading-relaxed">{resource.whyRelevant}</p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gunmetal/30">
                      <div className="flex gap-2 flex-wrap">
                        {resource.modules && resource.modules.length <= 4 && resource.modules.map(moduleNum => (
                          <span key={moduleNum} className="px-3 py-1 bg-gradient-to-r from-purple-700 to-pink-700 text-white text-xs rounded-full font-semibold shadow-md">
                            Module {moduleNum}
                          </span>
                        ))}
                        {resource.modules && resource.modules.length > 4 && (
                          <span className="px-3 py-1 bg-gradient-to-r from-purple-700 to-pink-700 text-white text-xs rounded-full font-semibold shadow-md">
                            All Modules
                          </span>
                        )}
                      </div>
                      <span className="px-3 py-1 bg-gradient-to-r from-blue-400 to-cyan-400 text-charcoal text-xs rounded-full font-semibold shadow-md">
                        {resource.category}
                      </span>
                    </div>

                    {resource.link && (
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                      >
                        <span>Visit Resource</span>
                        <span className="sr-only"> (opens in new tab)</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </>
                )}

                {/* Video & Podcast Resources */}
                {activeCategory === 'videos' && (
                  <>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center shadow-lg">
                        <Youtube className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-bright-gold mb-1">
                          {resource.title}
                        </h3>
                        <p className="text-pearl/60 text-sm">{resource.presenter}</p>
                        <p className="text-pearl/75 text-xs">{resource.platform} • {resource.duration}</p>
                      </div>
                    </div>

                    <p className="text-pearl/80 leading-relaxed mb-4">
                      {resource.description}
                    </p>

                    <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-500/5 border-l-4 border-yellow-400 rounded-lg p-4 mb-4 shadow-md">
                      <h4 className="text-yellow-400 font-bold text-sm mb-2 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Why Watch:
                      </h4>
                      <p className="text-pearl/80 text-sm leading-relaxed">{resource.whyWatch}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 border-l-4 border-green-400 rounded-lg p-4 mb-4 shadow-md">
                      <h4 className="text-green-400 font-bold text-sm mb-2 flex items-center gap-2">
                        <GraduationCap className="w-4 h-4" />
                        Key Lessons:
                      </h4>
                      <p className="text-pearl/80 text-sm leading-relaxed">{resource.keyLessons}</p>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gunmetal/30">
                      <div className="flex gap-2 flex-wrap">
                        {resource.modules && resource.modules.length <= 4 && resource.modules.map(moduleNum => (
                          <span key={moduleNum} className="px-3 py-1 bg-gradient-to-r from-purple-700 to-pink-700 text-white text-xs rounded-full font-semibold shadow-md">
                            Module {moduleNum}
                          </span>
                        ))}
                        {resource.modules && resource.modules.length > 4 && (
                          <span className="px-3 py-1 bg-gradient-to-r from-purple-700 to-pink-700 text-white text-xs rounded-full font-semibold shadow-md">
                            All Modules
                          </span>
                        )}
                      </div>
                      <span className="text-pearl/80 text-sm">{resource.year}</span>
                    </div>

                    {resource.link && (
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-lg transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                      >
                        <span>Go to Series</span>
                        <span className="sr-only"> (opens in new tab)</span>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* No Results */}
        {resources.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Search className="w-16 h-16 text-pearl/20 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-pearl mb-2">No resources found</h3>
            <p className="text-pearl/60">Try a different search term or category</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ResourcesPage;
